import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error('Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-8" style={{
      background: 'linear-gradient(135deg, #0a1f3d 0%, #0d3b66 50%, #1976d2 100%)'
    }}>
      <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full opacity-30 wave-float" style={{background: 'radial-gradient(circle, #06aed5 0%, transparent 70%)'}}></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20 wave-float" style={{background: 'radial-gradient(circle, #4ea8de 0%, transparent 70%)', animationDelay: '2s'}}></div>

      <div className="w-full max-w-md glass-card p-10 relative">
        <div className="mb-6">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 rounded-lg ocean-gradient flex items-center justify-center text-white font-bold text-xl" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>S</div>
            <h1 className="text-2xl font-bold ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>SAIL Books</h1>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            {sent ? 'Check your email' : 'Reset password'}
          </h2>
          <p className="mt-2 text-slate-600">
            {sent ? 'If an account exists, we sent reset instructions' : 'We\'ll send you a link to reset your password'}
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" className="mt-2 bg-white/60 border-slate-300" />
            </div>
            <Button type="submit" disabled={loading} className="w-full ocean-gradient text-white hover:opacity-90 h-12 font-semibold rounded-lg shadow-lg" data-testid="forgot-submit-button">
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        ) : (
          <div className="text-sm text-slate-600 space-y-3" data-testid="forgot-sent-message">
            <p>If <strong>{email}</strong> is registered with SAIL Books, you'll receive a password reset link shortly.</p>
            <p className="text-xs text-slate-500">Note: Email is mocked in this environment. Check backend logs for the reset link.</p>
          </div>
        )}

        <p className="mt-6 text-center text-sm text-slate-600">
          <Link to="/login" className="font-semibold text-blue-700 hover:underline" data-testid="back-to-login-link">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success('Password reset successfully');
      navigate('/login');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-8" style={{
      background: 'linear-gradient(135deg, #0a1f3d 0%, #0d3b66 50%, #1976d2 100%)'
    }}>
      <div className="w-full max-w-md glass-card p-10 relative">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Set new password</h2>
        <p className="text-slate-600 mb-6">Choose a strong password for your account</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">New Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password-input" className="mt-2 bg-white/60 border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Confirm Password</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="reset-confirm-input" className="mt-2 bg-white/60 border-slate-300" />
          </div>
          <Button type="submit" disabled={loading || !token} className="w-full ocean-gradient text-white hover:opacity-90 h-12 font-semibold rounded-lg shadow-lg" data-testid="reset-submit-button">
            {loading ? 'Resetting...' : 'Reset password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
